;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();import type { Step } from "react-joyride";
import { isTourLaunchPathActive } from "@/features/onboarding-tours/orchestrator/launch-tour";
import { getTourDefinition } from "@/features/onboarding-tours/registry/tour-registry";
import type {
	TourId,
	TourStep,
	TourStepBuildContext,
	TourStepData,
} from "@/features/onboarding-tours/types/tour-definition";

const TARGET_WAIT_TIMEOUT_MS = 3_000;
/** Slightly above target wait so Joyride does not abort navigation before the route settles. */
const TOUR_STEP_BEFORE_TIMEOUT_MS = 3_500;

export interface BuildTourStepsParams {
	tourId: TourId;
	workspaceId: string;
	framing?: TourStepBuildContext["framing"];
	currentPathname: string;
	currentSearch: string;
	ensureRoute: ({ route }: { route: string }) => Promise<void>;
}

function attachStepBeforeHooks({
	step,
	workspaceId,
	currentPathname,
	currentSearch,
	ensureRoute,
}: {
	step: TourStep;
	workspaceId: string;
	currentPathname: string;
	currentSearch: string;
	ensureRoute: ({ route }: { route: string }) => Promise<void>;
}): Step {
	const {
		effect,
		navigateBefore,
		scrollBefore,
		waitForTarget,
		blockTargetInteraction,
		before: originalBefore,
		...joyrideFields
	} = step;

	const stepData = (step.data ?? {}) as TourStepData;
	const isConditionallySkippedStep = stepData.skipWhenTargetHidden === true;
	const needsBeforeHook = Boolean(navigateBefore || originalBefore);

	const joyrideStep: Step = {
		...joyrideFields,
		beforeTimeout: navigateBefore
			? TOUR_STEP_BEFORE_TIMEOUT_MS
			: joyrideFields.beforeTimeout,
		blockTargetInteraction: blockTargetInteraction === true,
		data: {
			...stepData,
			tourScrollBefore: scrollBefore,
			tourStepEffect: effect,
		},
		disableFocusTrap: waitForTarget ? true : joyrideFields.disableFocusTrap,
		skipBeacon: joyrideFields.skipBeacon ?? true,
		targetWaitTimeout: isConditionallySkippedStep
			? 0
			: waitForTarget
				? TARGET_WAIT_TIMEOUT_MS
				: step.targetWaitTimeout,
	};

	if (!needsBeforeHook) {
		return joyrideStep;
	}

	joyrideStep.before = async (tourData) => {
		if (navigateBefore) {
			const route = navigateBefore({ workspaceId });
			const isActive = isTourLaunchPathActive({
				currentPathname,
				currentSearch,
				targetRoute: route,
			});

			if (!isActive) {
				await ensureRoute({ route });
			}
		}

		if (originalBefore) {
			await originalBefore(tourData);
		}
	};

	return joyrideStep;
}

export function buildTourSteps({
	tourId,
	workspaceId,
	framing = "default",
	currentPathname,
	currentSearch,
	ensureRoute,
}: BuildTourStepsParams): Step[] {
	const definition = getTourDefinition(tourId);
	if (!definition) {
		return [];
	}

	const context: TourStepBuildContext = { framing, workspaceId };
	const steps = definition.buildSteps(context);

	return steps.map((step) =>
		attachStepBeforeHooks({
			currentPathname,
			currentSearch,
			ensureRoute,
			step,
			workspaceId,
		}),
	);
}
